import type { ComponentProps } from 'react';
import React, { useEffect } from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import PopOver from '../../components/deprecated/PopOver';
import { EditorActions } from '../../editor/editorSlice';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { ExtractReactionThreadTypes } from '../../utils/post/threadUtils';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import MagneticWallItem from '../MagneticWallItem';
import type { CompactPopOverSide } from './getMagneticWallItemLayout';
import ReactionThreadCompact from './ReactionThreadCompact';
import ReactionThreadContent from './ReactionThreadContent';

const POST_FRAGMENT = gql(`
  fragment ReactionThread_post on RootPostFields {
    ...ReactionThreadContent_post
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment ReactionThread_thread on RootThreadFields {
    id
    ...ReactionThreadCompact_thread
    ...ReactionThreadContent_thread
  }
`);

type ReactionThreadProps = {
  post: DocumentType<typeof POST_FRAGMENT>;
  thread: ExtractReactionThreadTypes<typeof THREAD_FRAGMENT>;
  isFocused?: boolean;
  isPopOverOpen?: boolean;
  compactPopOverSide: CompactPopOverSide;
  overlapped: boolean;
  onResize: () => void;
  size: MagneticWallItemSize;
  onPopOverHeightChange: (height: number) => void;
} & Pick<
  React.ComponentProps<typeof ReactionThreadContent>,
  'unresolveThread' | 'deleteReactionThread' | 'addReaction' | 'deleteReaction'
>;

const ReactionThread = ({
  post,
  thread,
  isFocused = false,
  isPopOverOpen = false,
  compactPopOverSide,
  overlapped,
  onResize,
  size,
  unresolveThread,
  deleteReactionThread,
  addReaction,
  deleteReaction,
  onPopOverHeightChange,
}: ReactionThreadProps) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    onResize();
  }, [onResize]);

  const handleClick = () => {
    if (!isPopOverOpen) {
      dispatch(
        EditorActions.focusMagneticWall({
          id: thread.id,
          type: 'reaction',
          isActive: true,
          hasExpandIntent: false,
        }),
      );
    }
  };

  const handleBlur = () => {
    dispatch(EditorActions.blurMagneticWall());
  };

  const commonContentProps = {
    thread,
    post,
    unresolveThread,
    deleteReactionThread,
    addReaction,
    deleteReaction,
    onResize,
    expanded: isFocused,
    expand: handleClick,
  } as const satisfies Partial<ComponentProps<typeof ReactionThreadContent>>;

  if (size === 'compact') {
    return (
      <MagneticWallItem.Root
        id={`reaction-${thread.id}`}
        focused={isFocused}
        onClick={handleClick}
        compactView={
          <MagneticWallItem.ResponsivePopover
            onClose={handleBlur}
            target={
              <ReactionThreadCompact focused={isFocused} thread={thread} />
            }
            onPopOverHeightChange={onPopOverHeightChange}
            content={
              <PopOver.Container noPadding width="auto" overflow="visible">
                <ReactionThreadContent
                  asPopOver
                  overlapped={false}
                  {...commonContentProps}
                />
              </PopOver.Container>
            }
            compactPopOverSide={compactPopOverSide}
            open={isPopOverOpen}
            isPopOverMinWidthDisabled
          />
        }
        size="compact"
      />
    );
  }

  return (
    <ReactionThreadContent overlapped={overlapped} {...commonContentProps} />
  );
};

export default ReactionThread;
